import { SlimHero } from "components/HeroBlocks/SlimHero";
import { SearchInput } from "components/Input/Input";
import Layout from "components/Layout";
import { PageNavBar } from "components/PageNavBar";
import { ProductGrid } from "components/ProductGrid";
import { SortDropdown } from "components/wagtail/GridPage";
import {
    FetchError,
    readCMSDataClientSide,
    readCMSDataServerSide,
} from "helpers/api";
import { useQuery } from "helpers/query";
import urls from "helpers/urls";
import { DRFPaginatedQuery, MenuData } from "interfaces";
import { ProductGridTile } from "interfaces/products";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";

import { EmtekClientProvider } from "../queryClient";
import { getNavData } from "./[[...slug]]";

import styles from "./search.module.scss";

export const getServerSideProps: GetServerSideProps = async (context) => {
    let searchParams = new URL(
        context.resolvedUrl,
        `https://${context.req.headers.host}`,
    ).searchParams;

    let tiles: DRFPaginatedQuery<ProductGridTile> | null = null;
    let singleResult: string | null = null;

    if (searchParams.get("search")) {
        try {
            tiles = await readCMSDataServerSide("/api/gridsearch/", {
                search: searchParams,
                context: context,
            });
            if (tiles?.results.length === 1)
                singleResult = tiles.results[0].url;
        } catch (e) {
            if (e instanceof FetchError) {
                if (e.response.status === 400) {
                    // handle bad request
                }
            }
        }
    }

    if (!!singleResult)
        return { redirect: { destination: singleResult, permanent: false } };

    const navData = await getNavData(context);

    const ordering = searchParams.get("ordering") as
        | "default"
        | "title"
        | "-title"
        | null;

    // Pass tiles to the page via props
    return {
        props: {
            tiles,
            searchParam: searchParams.get("search"),
            menus: navData,
            ordering,
        },
    };
};

interface Props {
    tiles: DRFPaginatedQuery<ProductGridTile> | null;
    searchParam: string;
    menus: MenuData[];
    ordering: "default" | "title" | "-title" | null;
}

export default function SearchPageWrapper(props: Props) {
    return (
        <EmtekClientProvider>
            <SearchPage {...props} />
        </EmtekClientProvider>
    );
}

function SearchPage({
    searchParam,
    ordering,
    menus,
    tiles: initialTiles,
}: Props) {
    const router = useRouter();
    const title = !!searchParam
        ? `Search Results for: “${encodeURI(searchParam)}”`
        : "Search Emtek";

    const { data: tiles } = useQuery<DRFPaginatedQuery<ProductGridTile>>({
        namespaceID: menus[0].site,
        queryKey: ["tiles", router.query],
        queryFn: () => {
            return readCMSDataClientSide(`/api/gridsearch/`, {
                search: new URLSearchParams(window.location.search),
            });
        },
        initialData: initialTiles ?? undefined,
    });
    if (!tiles)
        throw new Error(
            "Tile data should always be present, either from initial data or aditional fetched data.",
        );

    return (
        <Layout menus={menus} metadata={{ title }}>
            <main>
                <SlimHero
                    cmsData={{
                        content: `<h1>${title}</h1>`,
                        media: { image: null, video: null },
                        background_color: "pewter1",
                        text_color: "auto",
                    }}
                />
                {!!searchParam && tiles !== null ? (
                    <div className={styles.gridPage}>
                        <section className={styles.topBar}>
                            <div className={styles.mobileSort}>
                                <SortDropdown />
                            </div>
                            <div className={styles.desktopTopBar}>
                                <span>{tiles.count} Products</span>
                                <SortDropdown
                                    className={styles.desktopSort}
                                    label="Sort by:"
                                />
                            </div>
                        </section>
                        <section>
                            <ProductGrid
                                tiles={tiles.results}
                                loading={false}
                                tilesPerRow={4}
                            />
                            <PageNavBar {...tiles} padBottom={true} />
                        </section>
                    </div>
                ) : (
                    <div className={styles.gridPage}>
                        <div className={styles.searchInputWrapper}>
                            <SearchInput
                                submit={(searchString) =>
                                    window.location.assign(
                                        `${
                                            urls.search
                                        }?search=${encodeURIComponent(
                                            searchString,
                                        )}`,
                                    )
                                }
                                placeholder="Search"
                                className={styles.searchInput}
                                collapsed={false}
                            />
                        </div>
                    </div>
                )}
            </main>
        </Layout>
    );
}
